import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useState } from 'react';

import { ProfesionalSelect } from '@/features/profesionales/components/ProfesionalSelect';
import { useNotify } from '@/hooks/useNotify';
import { isApiError } from '@/lib/apiError';

import { useCambiarProfesional } from '../hooks/useEstablecimientos';
import type { Establecimiento } from '../types';

interface CambiarProfesionalDialogProps {
  open: boolean;
  establecimiento: Establecimiento;
  profesionalActualNombre: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function CambiarProfesionalDialog({
  open,
  establecimiento,
  profesionalActualNombre,
  onClose,
  onSuccess,
}: CambiarProfesionalDialogProps): JSX.Element {
  const notify = useNotify();
  const [uidNuevo, setUidNuevo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useCambiarProfesional();

  function handleClose(): void {
    if (mutation.isPending) return;
    setUidNuevo('');
    setError(null);
    onClose();
  }

  async function handleConfirm(): Promise<void> {
    setError(null);

    if (!uidNuevo) {
      setError('Seleccioná un profesional');
      return;
    }
    if (uidNuevo === establecimiento.uid_profesional_actual) {
      setError('Es el mismo profesional que ya está asignado');
      return;
    }

    try {
      await mutation.mutateAsync({
        uid: establecimiento.uid_establecimiento,
        data: { uid_profesional_nuevo: uidNuevo },
      });
      notify.success('Profesional cambiado correctamente.');
      setUidNuevo('');
      onSuccess();
    } catch (err) {
      setError(
        isApiError(err)
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Error al cambiar el profesional'
      );
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cambiar profesional asignado</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            El establecimiento <strong>{establecimiento.nombre}</strong> está
            actualmente asignado a <strong>{profesionalActualNombre}</strong>.
          </DialogContentText>

          <Alert severity="info" variant="outlined">
            Los protocolos existentes mantienen su profesional original. Solo los
            protocolos nuevos quedarán asociados al profesional asignado.
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}

          <ProfesionalSelect
            label="Nuevo profesional"
            value={uidNuevo}
            onChange={(e) => setUidNuevo(e.target.value as string)}
            disabled={mutation.isPending}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          variant="contained"
          disabled={mutation.isPending || !uidNuevo}
        >
          {mutation.isPending ? 'Cambiando...' : 'Cambiar profesional'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
